import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message } from 'antd';
import { Detail } from 'components/Page';
import { load, reset, update } from 'actions';
import { canViewOptimiser } from 'library/optimiser';
import { isValidationBlockError } from 'actions/validationBlock';
import { loadViolationsForRecord as loadViolationsForRecordAction } from 'actions/optimiserStatus';
import OptimiserReadinessWarning from 'components/OptimiserReadinessWarning';
import usersConfig from 'views/Users/config';
import activityConfig from 'views/Roster/Activity/config';
import { handlePrepareData as prepareActivity } from 'views/Roster/Activity/page';
import * as permissions from 'library/permissions';

const RECORD_CONFIG = {
  user: {
    detailConfig: usersConfig.detail,
    itemKey: 'userAccount',
    prepareData: d => d,
    title: 'User',
    canAdmin: () => permissions.CanAdminUsers().hasPermissions()
  },
  activity: {
    detailConfig: activityConfig.detail,
    itemKey: 'activity',
    prepareData: prepareActivity,
    title: 'Activity',
    canAdmin: () => permissions.CanAdminActivities().hasPermissions()
  }
};

class RecordDetailHost extends Component {
  constructor(props) {
    super(props);
    this.state = { readinessViolations: null };
    this.handleSave = this.handleSave.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  componentDidMount() {
    this.loadRecord();
  }

  componentDidUpdate(prevProps) {
    const { recordType, recordId } = this.props;
    if (prevProps.recordType !== recordType || prevProps.recordId !== recordId) {
      this.loadRecord();
    }
  }

  loadRecord() {
    const { recordType, recordId, loadItem } = this.props;
    if (recordType && recordId) {
      const cfg = RECORD_CONFIG[recordType];
      if (cfg) loadItem(cfg.itemKey, { id: recordId });
    }
  }

  handleSave(id, data) {
    const {
      recordType, saveItem, onSaved, loadViolationsForRecord
    } = this.props;
    const cfg = RECORD_CONFIG[recordType];
    if (!cfg) return Promise.resolve();
    const prepared = cfg.prepareData(data);
    return saveItem(cfg.itemKey, id, prepared)
      .then((response) => {
        message.success('Saved successfully');
        if (onSaved) onSaved();
        if (recordType === 'user' && canViewOptimiser()) {
          loadViolationsForRecord({ recordId: id, recordType: 'user' })
            .then((violations) => {
              this.setState({ readinessViolations: violations });
            })
            .catch(() => {
              message.warning('Optimiser status check could not be completed');
            });
        }
        return response;
      })
      .catch((error) => {
        // A blocked write (FR-5.1) is already surfaced by the global validation dialog;
        // don't stack a redundant "Save failed" toast on top of it.
        if (!isValidationBlockError(error)) {
          message.error('Save failed');
        }
      });
  }

  // Closing mirrors Standard's cancel: reset the redux item then let the parent
  // clear its selection so the panel returns to the empty placeholder.
  handleClose() {
    const { recordType, resetItem, onClose } = this.props;
    const cfg = recordType ? RECORD_CONFIG[recordType] : null;
    if (cfg) {
      resetItem(cfg.itemKey);
    }
    onClose();
  }

  render() {
    const { recordType, recordId, item } = this.props;
    const { readinessViolations } = this.state;
    const cfg = recordType ? RECORD_CONFIG[recordType] : null;
    if (!cfg) {
      return (
        <Detail
          currentItem={{}}
          tabItems={[]}
          tabPosition="left"
          title="Record"
          emptyMessage="Select a failing record or publish attempt to view details"
        />
      );
    }
    const canAdmin = cfg.canAdmin();
    const isEditMode = !!recordId;
    const tabItems = cfg.detailConfig.tabItems
      .filter(tab => !tab.visible || tab.visible(item))
      .map((tab) => {
        const props = {
          canAdmin,
          dataSource: item || {},
          isEditMode,
          itemId: item ? item.id : null,
          objectId: item ? item.id : null,
          onSaveClick: (id, data) => this.handleSave(id, data),
          onCancelClick: () => this.handleClose(),
          commands: { ...cfg.detailConfig.commands },
          permissions
        };
        return {
          key: tab.key,
          title: tab.title,
          component: tab.component(props),
          forceRender: tab.forceRender
        };
      });
    return (
      <div style={{ width: '100%' }}>
        <Detail
          currentItem={item || {}}
          tabItems={tabItems}
          tabPosition="left"
          title={cfg.title}
          emptyMessage="Select a failing record or publish attempt to view details"
          isEditMode={isEditMode}
        />
        {readinessViolations && readinessViolations.length > 0 && (
          <OptimiserReadinessWarning
            violations={readinessViolations}
            onClose={() => this.setState({ readinessViolations: null })}
          />
        )}
      </div>
    );
  }
}

RecordDetailHost.propTypes = {
  recordType: PropTypes.string,
  recordId: PropTypes.number,
  item: PropTypes.object,
  loadItem: PropTypes.func,
  saveItem: PropTypes.func,
  resetItem: PropTypes.func,
  loadViolationsForRecord: PropTypes.func,
  onSaved: PropTypes.func,
  onClose: PropTypes.func
};

RecordDetailHost.defaultProps = {
  recordType: null,
  recordId: null,
  item: {},
  loadItem() {},
  saveItem() {},
  resetItem() {},
  loadViolationsForRecord() {
    return Promise.resolve([]);
  },
  onSaved() {},
  onClose() {}
};

const mapStateToProps = (state, ownProps) => {
  const cfg = ownProps.recordType ? RECORD_CONFIG[ownProps.recordType] : null;
  const item = cfg ? state.api[cfg.itemKey] && state.api[cfg.itemKey].data : {};
  return { item: item || {} };
};

const mapDispatchToProps = dispatch => ({
  loadItem(itemKey, params) {
    return dispatch(load(itemKey, params));
  },
  saveItem(itemKey, id, data) {
    return dispatch(update(itemKey, { id, ...data }));
  },
  resetItem(itemKey) {
    return dispatch(reset(itemKey));
  },
  loadViolationsForRecord(props) {
    return dispatch(loadViolationsForRecordAction(props));
  }
});

export { RecordDetailHost };
export default connect(mapStateToProps, mapDispatchToProps)(RecordDetailHost);
